import { useProfession } from "../lib/professions";
import React, { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import { PaperPlaneTilt, WhatsappLogo, Copy, EnvelopeSimple } from "@phosphor-icons/react";

export function ShareQuoteDialog({ quote, open, onOpenChange }) {
  const prof = useProfession();
  const [email, setEmail] = useState(quote?.patient?.email || "");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);

  const share = async (channel) => {
    setBusy(true);
    try {
      const r = await api.post(`/quotes/${quote.id}/share`, { channel, to_email: channel === "email" ? email : null, message });
      setLink(r.data.link);
      if (channel === "email") toast.success(`Orçamento enviado para ${email}`);
      if (channel === "whatsapp") {
        const phone = (quote.patient?.phone || "").replace(/\D/g, "");
        const target = phone ? `55${phone.replace(/^55/, "")}` : "";
        window.open(`https://wa.me/${target}?text=${encodeURIComponent(r.data.whatsapp_text)}`, "_blank");
      }
      if (channel === "link") { await navigator.clipboard?.writeText(r.data.link); toast.success("Link copiado!"); }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle>Enviar orçamento ao {prof.client}</DialogTitle>
          <DialogDescription>O {prof.client} recebe um link seguro para visualizar e baixar o PDF.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Mensagem (opcional)</Label>
            <Textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Ex.: Qualquer dúvida estou à disposição!" className="mt-1" data-testid="share-message" />
          </div>
          <div className="rounded-lg border border-slate-200 p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><EnvelopeSimple size={18} className="text-indigo-600" /> Por e-mail</div>
            <div className="flex gap-2">
              <Input type="email" placeholder={`email@${prof.client}.com`} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="share-email" />
              <Button onClick={() => share("email")} disabled={busy || !email} className="bg-indigo-600 hover:bg-indigo-700 shrink-0" data-testid="share-send-email"><PaperPlaneTilt size={16} className="mr-1" /> Enviar</Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Button variant="outline" onClick={() => share("whatsapp")} disabled={busy} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="share-whatsapp"><WhatsappLogo size={18} className="mr-1" /> WhatsApp</Button>
            <Button variant="outline" onClick={() => share("link")} disabled={busy} data-testid="share-copy-link"><Copy size={18} className="mr-1" /> Copiar link</Button>
          </div>
          {link && <code className="block text-xs bg-slate-50 border border-slate-200 rounded px-3 py-2 truncate" data-testid="share-link">{link}</code>}
          {quote.views > 0 && <p className="text-xs text-slate-500">Visualizado {quote.views}x pelo {prof.client}{quote.last_viewed_at ? ` · último acesso ${new Date(quote.last_viewed_at).toLocaleString("pt-BR")}` : ""}</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
